import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
import AdminSidebar from "../components/layout/AdminSidebar";
import AdminTopbar, {
  AdminNotification,
} from "../components/layout/AdminTopbar";
import { useAuth } from "../context/AuthContext";
import { registerAdminWebPush } from "../lib/webPush";

const STORAGE_KEY = "lynx_admin_notifications";

const shellStyle: React.CSSProperties = {
  minHeight: "100vh",
  background: "var(--background)",
  color: "var(--text-primary)",
  display: "flex",
};

const contentShellStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
};

const mainStyle: React.CSSProperties = {
  flex: 1,
  padding: "24px",
};

const innerContentStyle: React.CSSProperties = {
  width: "100%",
};

function readStoredNotifications(): AdminNotification[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveStoredNotifications(notifications: AdminNotification[]) {
  window.localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify(notifications.slice(0, 50)),
  );
}

function normalizePushPayload(payload: any): AdminNotification {
  const notification = payload?.notification || payload?.message || payload || {};

  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    title: notification.title || "Lynx",
    body: notification.body || "Nueva notificación",
    data: notification.data || {},
    receivedAt: notification.receivedAt || new Date().toISOString(),
    read: false,
  };
}

const AdminLayout = () => {
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<AdminNotification[]>(() =>
    typeof window === "undefined" ? [] : readStoredNotifications(),
  );

  const { token, userId } = useAuth();

  const unreadNotificationsCount = useMemo(
    () => notifications.filter((notification) => !notification.read).length,
    [notifications],
  );

  const handleToggleSidebar = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  const addNotification = useCallback((notification: AdminNotification) => {
    setNotifications((prev) => {
      const next = [notification, ...prev].slice(0, 50);
      saveStoredNotifications(next);
      return next;
    });
  }, []);

  const markNotificationsAsRead = useCallback(() => {
    setNotifications((prev) => {
      const next = prev.map((notification) => ({
        ...notification,
        read: true,
      }));

      saveStoredNotifications(next);
      return next;
    });
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    saveStoredNotifications([]);
  }, []);

  useEffect(() => {
    registerAdminWebPush({ token, userId }).catch((error) => {
      console.error("[Lynx Push] Error inicializando web push", error);
    });
  }, [token, userId]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type !== "LYNX_PUSH_RECEIVED") return;

      console.log("[Lynx Push] Notificación recibida en web app", event.data.payload);

      addNotification(normalizePushPayload(event.data.payload));
    };

    navigator.serviceWorker.addEventListener(
      "message",
      handleServiceWorkerMessage,
    );

    return () => {
      navigator.serviceWorker.removeEventListener(
        "message",
        handleServiceWorkerMessage,
      );
    };
  }, [addNotification]);

  return (
    <div style={shellStyle}>
      <AdminSidebar collapsed={collapsed} />

      <div style={contentShellStyle}>
        <AdminTopbar
          collapsed={collapsed}
          onToggleSidebar={handleToggleSidebar}
          notifications={notifications}
          unreadNotificationsCount={unreadNotificationsCount}
          onMarkNotificationsAsRead={markNotificationsAsRead}
          onClearNotifications={clearNotifications}
        />

        <main style={mainStyle}>
          <div style={innerContentStyle}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;